import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { api } from '../../lib/api';
import { notifyAccountSessionChanged, useAccountSession } from '../../lib/accountSession';
import { useResource } from '../../lib/useResource';
import type { ApiEnvelope } from '../../lib/types';

export interface AccountMe {
  account: {
    id: string; email: string; status: string; locale: string; timezone: string | null;
    mfa_enabled: boolean; email_verified_at: string | null;
  };
  linked_games: { game_id: string; principal_id: string; platform: string; last_seen_at: string | null }[];
  unread_inbox: number;
}

const NAV = [
  { to: '/account', label: '계정 홈', end: true },
  { to: '/account/connections', label: '게임 연결' },
  { to: '/account/inbox', label: '선물함' },
  { to: '/account/entitlements', label: '구매·보상' },
  { to: '/account/security', label: '보안' },
  { to: '/account/preferences', label: '설정' },
  { to: '/account/data', label: '데이터·탈퇴' },
];

/** Shared chrome for the signed-in account area (U005–U012). */
export function AccountLayout({ title, lead, children }: { title: string; lead?: string; children: ReactNode }) {
  const navigate = useNavigate();
  const session = useAccountSession();

  async function signOut() {
    await api.account('/logout', { method: 'POST' });
    notifyAccountSessionChanged();
    navigate('/account/sign-in');
  }

  return (
    <div className="account-layout">
      <aside className="account-nav">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}
            className={({ isActive }) => (isActive ? 'account-nav__link is-active' : 'account-nav__link')}>
            {item.label}
          </NavLink>
        ))}
        {session.state === 'signed_in' && (
          <button type="button" className="account-nav__link account-nav__link--action" onClick={signOut}>
            로그아웃
          </button>
        )}
      </aside>

      <div>
        <header className="site-head site-head--tight">
          <h1 className="site-head__title">{title}</h1>
          {lead && <p className="site-head__lead">{lead}</p>}
        </header>
        {children}
      </div>
    </div>
  );
}

/** Resolves game ids to names once, for every account screen that shows per-game rows. */
export function useGameNames() {
  const games = useResource<{ id: string; slug: string; name: string }[]>(
    (signal) => api.public('/api/v1/games', { signal, query: { per_page: 60 } }) as Promise<ApiEnvelope<{ id: string; slug: string; name: string }[]>>,
    [],
  );

  const map = new Map((games.data ?? []).map((game) => [game.id, game]));

  return {
    name: (id: string | null) => (id ? map.get(id)?.name ?? '이름 미확인 게임' : '게임 미지정'),
    slug: (id: string | null) => (id ? map.get(id)?.slug ?? null : null),
  };
}
